import { useEffect, useRef, useState } from 'react';
import LogDownloadService from './service';
import {
    CHECK_STATUS_INTERVAL,
    MODE,
    REPORT_API_STATUS,
    RETRY_REQUEST_COUNT,
    STATUS,
    VALIDATION_TYPE,
} from './constants';

const service = new LogDownloadService();

const getQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
        taskId: params.get('task_id'),
        reportId: params.get('report_id'),
        logType: params.get('log_type'),
        apiType: params.get('api_type'),
        validationId: params.get('validation_id'),
        validationType: params.get('validation_type'),
    };
};

const selectMode = ({ validationId, reportId }) => {
    if (validationId) return MODE.AUDIT;
    if (reportId) return MODE.REPORT;
    return MODE.EXPORT;
};

const normalizeStatus = (mode, serverStatus) => {
    if (mode !== MODE.REPORT) return serverStatus;
    switch (serverStatus) {
        case REPORT_API_STATUS.QUEUED:
            return STATUS.PENDING;
        case REPORT_API_STATUS.STARTED:
            return STATUS.STARTED;
        case REPORT_API_STATUS.DONE:
            return STATUS.SUCCESS;
        case REPORT_API_STATUS.FAILED:
            return STATUS.FAILED;
        default:
            return serverStatus;
    }
};

const isInProgress = status => status === STATUS.STARTING || status === STATUS.PENDING || status === STATUS.STARTED;

const requestFor = (mode, query) => {
    if (mode === MODE.AUDIT) {
        return service.getAuditDetails(query.validationId, query.validationType || VALIDATION_TYPE.SDK);
    }
    if (mode === MODE.REPORT) {
        return service.getReportDetails(query.reportId);
    }
    return service.getTaskDetails(query.taskId, query.logType, query.apiType);
};

export default function useDownloadPolling() {
    const [query] = useState(getQueryParams);
    const [mode] = useState(() => selectMode(query));

    const [status, setStatus] = useState(STATUS.STARTING);
    const [link, setLink] = useState(null);
    const [isGsheet, setIsGsheet] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(undefined);
    const [validationValid, setValidationValid] = useState(false);

    const statusRef = useRef(STATUS.STARTING);
    const failedCountRef = useRef(0);

    useEffect(() => {
        let pollTimer = null;
        let cancelled = false;

        const applyStatus = next => {
            statusRef.current = next;
            setStatus(next);
        };

        const handleLink = resolvedLink => {
            if (!resolvedLink) return;
            if (mode === MODE.AUDIT && resolvedLink === '1') {
                setValidationValid(true);
                return;
            }
            setLink(resolvedLink);
            window.location.href = resolvedLink;
            if (mode === MODE.REPORT) {
                window.mixpanel?.track('Report Query - Download Started', {
                    link: resolvedLink,
                    report_id: query.reportId,
                });
            }
        };

        const shouldRetry = () => {
            failedCountRef.current += 1;
            if (failedCountRef.current > RETRY_REQUEST_COUNT) {
                applyStatus(STATUS.FAILED);
                return false;
            }
            return true;
        };

        function poll() {
            requestFor(mode, query)
                .then(data => {
                    if (cancelled) return;
                    if (data && data.status === 0) {
                        const details = data.value || {};
                        const nextStatus = normalizeStatus(mode, details.status);
                        setIsGsheet(Boolean(details.is_export_gsheet));
                        setShowErrorMessage(details.show_error_message);

                        if (statusRef.current !== nextStatus) {
                            applyStatus(nextStatus);
                            handleLink(details.link || details.download_url);
                        }
                        if (isInProgress(statusRef.current)) {
                            pollTimer = setTimeout(poll, CHECK_STATUS_INTERVAL);
                        }
                    } else if (shouldRetry()) {
                        pollTimer = setTimeout(poll, CHECK_STATUS_INTERVAL);
                    }
                })
                .catch(() => {
                    if (cancelled) return;
                    if (shouldRetry()) {
                        pollTimer = setTimeout(poll, CHECK_STATUS_INTERVAL);
                    }
                });
        }

        poll();

        return () => {
            cancelled = true;
            if (pollTimer) clearTimeout(pollTimer);
        };
    }, [mode, query]);

    return {
        status,
        displayStatus: status === STATUS.SUCCESS ? 'Done!' : status,
        link,
        isGsheet,
        showErrorMessage,
        validationValid,
        mode,
        validationType: query.validationType,
        logType: query.logType,
    };
}
